/* eslint-disable import/order */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Visibility, CalendarToday } from '@mui/icons-material';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useTranslation } from 'react-i18next';

import {
  useHistorialServicio,
  getServicioNombre,
  getReunionStatusColor,
} from '../../hooks/useReuniones';
import { Reunion } from '../../types/reuniones';
import { SERVICIOS_RECURRENTES } from '../../hooks/useReuniones';

dayjs.extend(utc);
dayjs.extend(timezone);
const TZ = 'Europe/Madrid';

interface Props {
  alumnoId: number;
  servicioId?: number;
  onViewReunion?: (reunion: Reunion) => void;
}

export default function HistorialReunionesServicio({ alumnoId, servicioId, onViewReunion }: Props) {
  const { t } = useTranslation<'translation'>();
  const [selectedServicio, setSelectedServicio] = useState<number>(
    servicioId || SERVICIOS_RECURRENTES.APOYO_PSICOLOGICO,
  );

  const {
    data: historial,
    isLoading,
    error,
  } = useHistorialServicio({
    alumnoId,
    servicioId: selectedServicio,
  });

  const servicioNombre = getServicioNombre(selectedServicio);

  const formatFecha = (fecha: string | Date) => {
    return dayjs(fecha).tz(TZ).locale('es').format('DD/MM/YYYY HH:mm');
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error al cargar el historial de reuniones
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <FormControl sx={{ minWidth: 250 }}>
          <InputLabel>Servicio</InputLabel>
          <Select
            value={selectedServicio}
            label="Servicio"
            onChange={(e) => setSelectedServicio(Number(e.target.value))}
          >
            <MenuItem value={SERVICIOS_RECURRENTES.APOYO_PSICOLOGICO}>
              {getServicioNombre(SERVICIOS_RECURRENTES.APOYO_PSICOLOGICO)}
            </MenuItem>
            <MenuItem value={SERVICIOS_RECURRENTES.COUNSELING}>
              {getServicioNombre(SERVICIOS_RECURRENTES.COUNSELING)}
            </MenuItem>
          </Select>
        </FormControl>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Historial: {servicioNombre}
        </Typography>
      </Stack>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : !historial || historial.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          No hay reuniones registradas para este servicio
        </Alert>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Staff</TableCell>
                <TableCell>Canal</TableCell>
                <TableCell>Posición</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {historial.map((reunion: Reunion) => {
                const fechaInicio = reunion.fechaInicio ? formatFecha(reunion.fechaInicio) : '—';
                const estado = reunion.estado || '—';
                const staffNombre =
                  (reunion.staff as any)?.nombreCompleto || (reunion.staff as any)?.nombre || '—';
                const canal = reunion.canal || '—';
                const tieneAnterior = !!reunion.idReunionAnterior;
                const tieneSiguiente = !!reunion.idReunionSiguiente;
                const posicionInfo = tieneAnterior || tieneSiguiente ? 'En cadena' : '—';

                return (
                  <TableRow key={reunion.id} hover>
                    <TableCell>{reunion.id}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">{fechaInicio}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={estado}
                        size="small"
                        sx={{
                          backgroundColor: getReunionStatusColor(estado),
                          color: 'white',
                          fontWeight: 500,
                        }}
                      />
                    </TableCell>
                    <TableCell>{staffNombre}</TableCell>
                    <TableCell>{canal}</TableCell>
                    <TableCell>
                      {posicionInfo !== '—' && (
                        <Chip
                          label={posicionInfo}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                      {posicionInfo === '—' && '—'}
                    </TableCell>
                    <TableCell align="center">
                      {onViewReunion && (
                        <Tooltip title="Ver detalles">
                          <IconButton
                            size="small"
                            onClick={() => onViewReunion(reunion)}
                            color="primary"
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
